import React, { useState, useEffect } from "react";
import { handleApiError } from "../../../common/utils";
import { toast } from "react-toastify";
import { SendActionModalProps } from "../types/types";
import {
  updateIncident,
  getRCAByIncidentId,
  updateRCA,
  sendNotificationEmail,
} from "../services/service";
import { formatDate } from "../utils/utils";
import { useOrgUsersStore } from "../../../store/store";
import { usePermissions } from "../../../hooks/usePermission";
import { INCIDENTS_MODULE } from "../../../constants/modules";
import { useInvalidateIncidents } from "../hooks/useIncidentQueries";

const SendActionModal: React.FC<SendActionModalProps> = ({
  data,
  onActionComplete,
  mode = "create",
  moduleKey,
}) => {
  const { canUpdate, canCreate } = usePermissions();
  const hasUpdatePerm = canUpdate(moduleKey || INCIDENTS_MODULE);
  const hasCreatePerm = canCreate(moduleKey || INCIDENTS_MODULE);
  const hasActionPerm = hasUpdatePerm || hasCreatePerm;
  const invalidateIncidents = useInvalidateIncidents();
  const [rcaRoles, setRcaRoles] = useState<
    { name: string; email: string; department: string; isLead: boolean }[]
  >([]);
  
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [isLeadRole, setIsLeadRole] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState(false);
  
  const { orgUsersData, fetchOrgUsers } = useOrgUsersStore();

  useEffect(() => {
    fetchOrgUsers();
  }, [fetchOrgUsers]);

  const handleAddRole = () => {
    if (!selectedUserId) {
      toast.error("Please select a user");
      return;
    }

    const user = orgUsersData.find((u: any) => (u._id || u.id) === selectedUserId);
    if (!user) return;

    if (rcaRoles.some((r) => r.email === user.email)) {
      toast.error("This user is already assigned to the RCA team");
      return;
    }

    if (isLeadRole && rcaRoles.some((r) => r.isLead)) {
      toast.error("An RCA Lead has already been assigned. Only one lead is allowed.");
      return;
    }

    const userName = `${user.firstName || user.name?.split(" ")[0] || ""} ${
      user.lastName || user.name?.split(" ")[1] || ""
    }`.trim();

    setRcaRoles([
      ...rcaRoles,
      {
        name: userName || user.email,
        email: user.email,
        department: user.department || "",
        isLead: isLeadRole,
      },
    ]);

    setSelectedUserId("");
    setIsLeadRole(false);
  };

  const handleRemoveRole = (index: number) => {
    if (mode === "view") return;
    setRcaRoles(rcaRoles.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === "view") return;

    if (rcaRoles.length === 0) {
      toast.error("Please assign at least one member to the RCA Team");
      return;
    }

    if (!rcaRoles.some((r) => r.isLead)) {
      toast.error("Please assign an RCA Lead");
      return;
    }

    try {
      setSubmitting(true);

      if (!data || !data.id) {
        toast.error("Invalid incident data");
        return;
      }

      // 1. Fetch the Auto-Created RCA for this incident
      const rcaResponse = await getRCAByIncidentId(data.id);
      if (rcaResponse.success && rcaResponse.data) {
        const rcaRecordId = rcaResponse.data.id;

        // 2. Format the team members to match GenericRCAModal expectations
        const formattedTeamMembers = rcaRoles.map((role, index) => ({
          srNo: index + 1,
          name: role.name,
          department: role.department,
          isLead: role.isLead,
        }));

        // 3. Update the RCA record with the assigned team
        await updateRCA(rcaRecordId, {
          rcaTeamMembers: formattedTeamMembers,
          status: "In Progress"
        });
      } else {
        toast.warn("No linked RCA found to update. Proceeding with incident update.");
      }

      // 4. Update the Incident Status to Pending
      await updateIncident(data.id, {
        status: "Pending",
      });

      // 5. Notify the Assigned Team Members via Email
      const emailsToNotify = rcaRoles.map((r) => r.email).filter(Boolean);
      if (emailsToNotify.length > 0) {
        await sendNotificationEmail({
          to: emailsToNotify,
          subject: `Action Required: RCA Team Assignment for Incident ${data?.incidentNumber || ""}`,
          body: `You have been assigned to the Root Cause Analysis (RCA) team for Incident ${
            data?.incidentNumber || ""
          }. Please log in to the portal to begin the RCA process.`,
        });
      }

      toast.success("RCA Team assigned successfully and incident moved to Pending.");
      invalidateIncidents();

      setTimeout(() => {
        if (onActionComplete) onActionComplete();
      }, 500);

    } catch (error: any) {
      handleApiError(error, "Failed to assign RCA team. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const userOptions = orgUsersData.map((user: any) => ({
    id: user._id || user.id,
    name: `${user.name || user.userName || ""} - ${user.email || ""}`,
  }));

  const hasLeadAssigned = rcaRoles.some((r) => r.isLead);

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 px-6 gap-4 text-sm pt-4">
        <div>
          <p className="text-black font-semibold">Incident Number</p>
          <p className="font-medium text-[#1b428a]">{data?.incidentNumber}</p>
        </div>
        <div>
          <p className="text-black font-semibold">Reported Date</p>
          <p className="font-medium text-[#1b428a]">
            {data?.reportingDate ? formatDate(data.reportingDate) : data?.reportDate}
          </p>
        </div>
        <div>
          <p className="text-black font-semibold">Department</p>
          <p className="font-medium text-[#1b428a]">{data?.department}</p>
        </div>
        <div>
          <p className="text-black font-semibold">Reported By</p>
          <p className="font-medium text-[#1b428a]">{data?.reportedBy}</p>
        </div>
      </div>
      
      <div className="flex-1 mt-6">
        <form onSubmit={handleSubmit} className="flex flex-col h-full">
          <div className="p-6 pb-24 space-y-6 flex-1 overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
              Assign RCA Team Members
            </h3>

            {/* Role Assignment Selection */}
            {mode !== "view" && (
              <div className="bg-blue-50 p-4 border border-blue-100 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Assign To User
                    </label>
                    <select
                      value={selectedUserId}
                      onChange={(e) => setSelectedUserId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                    >
                      <option value="">Select User</option>
                      {userOptions.map((user: { id: string; name: string }) => (
                        <option key={user.id} value={user.id}>
                          {user.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex flex-col justify-end pb-2">
                    <label 
                      className={`flex items-center space-x-2 ${
                        hasLeadAssigned ? "cursor-not-allowed opacity-60" : "cursor-pointer"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isLeadRole}
                        onChange={(e) => setIsLeadRole(e.target.checked)}
                        disabled={hasLeadAssigned}
                        className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 disabled:bg-gray-200"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Assign as RCA Lead
                      </span>
                    </label>
                    {hasLeadAssigned && (
                      <span className="text-xs text-amber-600 mt-1 italic">
                        Lead already assigned
                      </span>
                    )}
                  </div>
                </div>

                <div className="mt-4">
                  <button
                    type="button"
                    onClick={handleAddRole}
                    disabled={!selectedUserId}
                    className="px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-md hover:bg-teal-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    Add Team Member
                  </button>
                </div>
              </div>
            )}

            {/* Configured Roles List */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3">
                Configured RCA Team ({rcaRoles.length})
              </h4>
              
              {rcaRoles.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No team members assigned yet.</p>
              ) : (
                <div className="space-y-2">
                  {rcaRoles.map((role, index) => (
                    <div
                      key={index}
                      className={`flex items-center justify-between p-3 border rounded-md ${
                        role.isLead ? "bg-amber-50 border-amber-200" : "bg-white border-gray-200"
                      }`}
                    >
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-900">
                          {role.name}
                          {role.isLead && (
                            <span className="ml-2 px-2 py-0.5 text-xs font-semibold bg-amber-100 text-amber-800 rounded-full">
                              Lead
                            </span>
                          )}
                        </span>
                        <span className="text-xs text-gray-500">{role.email}</span>
                      </div>
                      
                      {mode !== "view" && (
                        <button
                          type="button"
                          onClick={() => handleRemoveRole(index)}
                          className="text-red-500 hover:text-red-700 text-sm font-medium px-2 py-1 rounded transition-colors"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer Submit Button */}
          {mode !== "view" && hasActionPerm && (
            <div className="fixed bottom-0 w-full max-w-5xl px-6 py-4 bg-[#eef4f8] border-t flex justify-start z-10">
              <button
                type="submit"
                className="bg-teal-600 text-white py-2 px-8 rounded-full font-semibold hover:bg-teal-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors shadow-sm"
                disabled={submitting || rcaRoles.length === 0}
              >
                {submitting ? "SUBMITTING..." : "CONFIRM ASSIGNMENT & SEND TO RCA"}
              </button>
            </div>
          )}
        </form>
      </div>
    </>
  );
};

export default SendActionModal;
